/**
 * Subscription Email Detection Service
 *
 * Robust, config-driven Gmail search and filtering pipeline
 * NO hardcoded vendors - uses patterns and signals
 */

import { SUBSCRIPTION_EMAIL_CONFIG } from '../config/subscriptionPatterns.js';

/**
 * Build Gmail search queries from config
 * Returns multiple targeted queries to capture all subscription email types
 */
export const buildGmailSearchQueries = () => {
  const config = SUBSCRIPTION_EMAIL_CONFIG.search;

  // Calculate date filter
  const daysAgo = new Date();
  daysAgo.setDate(daysAgo.getDate() - config.daysBack);
  const afterDate = daysAgo.toISOString().split('T')[0].replace(/-/g, '/');

  // Build FROM pattern
  const fromPattern = config.fromPatterns.map(p => `from:${p}`).join(' OR ');

  // Build subject keyword groups
  const allSubjectKeywords = Object.values(config.subjectKeywords).flat();
  const subjectPattern = allSubjectKeywords.map(k => `subject:"${k}"`).join(' OR ');

  // Build exclusions
  const exclusions = [
    ...config.excludeLabels.map(label => `-in:${label}`),
    ...config.excludeKeywords.map(kw => `-"${kw}"`)
  ].join(' ');

  const queries = [];

  // Query 1: Transactional senders + subscription keywords
  queries.push({
    name: 'Transactional Senders with Subscription Keywords',
    query: `(${fromPattern}) (${subjectPattern}) ${exclusions} after:${afterDate}`,
    priority: 1
  });

  // Query 2: Invoice/Receipt with PDF attachment
  if (config.attachments.enabled) {
    const attachmentKeywords = config.attachments.combinedWithKeywords
      .map(k => `subject:"${k}"`)
      .join(' OR ');
    queries.push({
      name: 'PDF Invoices/Receipts',
      query: `has:attachment filename:pdf (${attachmentKeywords}) ${exclusions} after:${afterDate}`,
      priority: 1
    });
  }

  // Query 3: High-priority event types (cancellation, payment failed)
  const criticalKeywords = [
    ...config.subjectKeywords.cancellation,
    ...config.subjectKeywords.paymentFailed
  ].map(k => `subject:"${k}"`).join(' OR ');

  queries.push({
    name: 'Critical Events (Cancellations & Payment Failures)',
    query: `(${criticalKeywords}) ${exclusions} after:${afterDate}`,
    priority: 2
  });

  // Query 4: Renewal + next billing indicators
  const renewalKeywords = [
    ...config.subjectKeywords.renewal,
    'next billing',
    'will be charged'
  ].map(k => `"${k}"`).join(' OR ');

  queries.push({
    name: 'Renewals & Next Billing',
    query: `(${renewalKeywords}) ${exclusions} after:${afterDate}`,
    priority: 2
  });

  console.log(`📋 Built ${queries.length} Gmail search queries`);
  queries.forEach((q, i) => {
    console.log(`   ${i + 1}. ${q.name} (Priority ${q.priority})`);
  });

  return queries;
};

/**
 * Extract headers and metadata from Gmail message
 */
const extractEnvelope = (message) => {
  const headers = message.payload.headers || [];
  const getHeader = (name) => headers.find(h => h.name.toLowerCase() === name.toLowerCase())?.value || '';

  return {
    messageId: getHeader('Message-ID'),
    threadId: message.threadId,
    from: getHeader('From'),
    returnPath: getHeader('Return-Path'),
    autoSubmitted: getHeader('Auto-Submitted'),
    subject: getHeader('Subject'),
    date: getHeader('Date'),
    mimeType: message.payload.mimeType
  };
};

/**
 * Check for PDF attachments and invoice indicators
 */
const extractAttachmentSignals = (message) => {
  const signals = {
    hasPdf: false,
    hasInvoiceFilename: false,
    attachmentCount: 0
  };

  const checkParts = (parts) => {
    if (!parts) return;

    for (const part of parts) {
      if (part.filename) {
        signals.attachmentCount++;
        const filename = part.filename.toLowerCase();

        if (part.mimeType === 'application/pdf' || filename.endsWith('.pdf')) {
          signals.hasPdf = true;

          if (filename.includes('invoice') || filename.includes('receipt')) {
            signals.hasInvoiceFilename = true;
          }
        }
      }

      if (part.parts) {
        checkParts(part.parts);
      }
    }
  };

  if (message.payload.parts) {
    checkParts(message.payload.parts);
  }

  return signals;
};

/**
 * Decode email body
 */
const decodeBody = (str) => {
  try {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) {
      base64 += '=';
    }
    return atob(base64);
  } catch (error) {
    return '';
  }
};

/**
 * Extract plain text body from message
 */
const extractBody = (message) => {
  let body = '';

  const getPart = (part) => {
    if (part.mimeType === 'text/plain' && part.body?.data) {
      body += decodeBody(part.body.data) + '\n';
    } else if (part.mimeType === 'text/html' && part.body?.data && !body) {
      const html = decodeBody(part.body.data);
      body += html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ') + '\n';
    }

    if (part.parts) {
      part.parts.forEach(getPart);
    }
  };

  if (message.payload.parts) {
    message.payload.parts.forEach(getPart);
  } else if (message.payload.body?.data) {
    body = decodeBody(message.payload.body.data);
  }

  return body.substring(0, 10000); // Limit body length for performance
};

/**
 * Extract HTML body for email rendering (preserves HTML formatting)
 */
export const extractHtmlBody = (message) => {
  let htmlBody = '';

  const getPart = (part) => {
    // Prioritize HTML content
    if (part.mimeType === 'text/html' && part.body?.data) {
      const html = decodeBody(part.body.data);
      if (!htmlBody || html.length > htmlBody.length) {
        htmlBody = html;
      }
    }

    if (part.parts) {
      part.parts.forEach(getPart);
    }
  };

  if (message.payload.parts) {
    message.payload.parts.forEach(getPart);
  } else if (message.payload.body?.data) {
    // Check if the main body is HTML
    if (message.payload.mimeType === 'text/html') {
      htmlBody = decodeBody(message.payload.body.data);
    }
  }

  return htmlBody.substring(0, 50000); // Limit to 50KB for HTML
};

/**
 * Score keywords in text
 */
const scoreKeywords = (text, keywords, weight) => {
  if (!text) return 0;

  const lowerText = text.toLowerCase();
  let score = 0;
  const matchedKeywords = [];

  for (const keyword of keywords) {
    if (lowerText.includes(keyword.toLowerCase())) {
      score += weight;
      matchedKeywords.push(keyword);
    }
  }

  return { score, matchedKeywords };
};

/**
 * Multi-stage filtering pipeline with confidence scoring
 */
export const filterAndScoreEmail = (message) => {
  const config = SUBSCRIPTION_EMAIL_CONFIG.filtering;
  const envelope = extractEnvelope(message);
  const attachmentSignals = extractAttachmentSignals(message);
  const body = extractBody(message);

  let totalScore = 0;
  const rulesFired = [];
  const matchedKeywords = { subject: [], body: [] };

  // === STAGE 1: Envelope-level signals ===

  // Auto-Submitted header
  if (envelope.autoSubmitted?.toLowerCase() === config.envelopeSignals.autoSubmittedHeader.requiredValue) {
    totalScore += config.envelopeSignals.autoSubmittedHeader.weight;
    rulesFired.push('auto-submitted-header');
  }

  // Transactional domain in From
  const fromLower = envelope.from.toLowerCase();
  if (config.envelopeSignals.transactionalDomains.patterns.some(p => fromLower.includes(p))) {
    totalScore += config.envelopeSignals.transactionalDomains.weight;
    rulesFired.push('transactional-domain');
  }

  // Return-Path matches From domain
  if (envelope.returnPath && envelope.from) {
    const fromDomain = envelope.from.match(/@([^>]+)/)?.[1];
    const returnDomain = envelope.returnPath.match(/@([^>]+)/)?.[1];
    if (fromDomain && returnDomain && fromDomain === returnDomain) {
      totalScore += config.envelopeSignals.returnPathMatch.weight;
      rulesFired.push('return-path-match');
    }
  }

  // === STAGE 2: Keyword scoring ===

  // Subject keywords
  for (const [category, keywords] of Object.entries(SUBSCRIPTION_EMAIL_CONFIG.search.subjectKeywords)) {
    const weight = config.keywordWeights.subject[category];
    if (weight) {
      const result = scoreKeywords(envelope.subject, keywords, weight);
      totalScore += result.score;
      matchedKeywords.subject.push(...result.matchedKeywords);
      if (result.matchedKeywords.length > 0) {
        rulesFired.push(`subject-${category}`);
      }
    }
  }

  // Body keywords
  for (const [category, keywords] of Object.entries(SUBSCRIPTION_EMAIL_CONFIG.search.subjectKeywords)) {
    const weight = config.keywordWeights.body[category];
    if (weight) {
      const result = scoreKeywords(body, keywords, weight);
      totalScore += result.score;
      matchedKeywords.body.push(...result.matchedKeywords);
      if (result.matchedKeywords.length > 0) {
        rulesFired.push(`body-${category}`);
      }
    }
  }

  // === STAGE 3: Attachment signals ===

  if (attachmentSignals.hasPdf) {
    if (attachmentSignals.hasInvoiceFilename ||
        envelope.subject.toLowerCase().includes('invoice') ||
        envelope.subject.toLowerCase().includes('receipt')) {
      totalScore += config.attachmentSignals.pdfInvoice.weight;
      rulesFired.push('pdf-invoice');
    } else {
      totalScore += config.attachmentSignals.anyPdf.weight;
      rulesFired.push('pdf-attachment');
    }
  }

  // === STAGE 4: Anti-patterns (negative scoring) ===

  const fullText = `${envelope.subject} ${body}`.toLowerCase();

  for (const [pattern, rules] of Object.entries(config.antiPatterns)) {
    if (rules.keywords.some(kw => fullText.includes(kw.toLowerCase()))) {
      totalScore += rules.weight; // Note: weight is negative
      rulesFired.push(`anti-${pattern}`);
    }
  }

  // === STAGE 5: Normalize score to 0-1 ===

  // Clamp score between 0 and 1
  const confidence = Math.max(0, Math.min(1, totalScore));

  // === STAGE 6: Event categorization ===

  const eventType = categorizeEvent(envelope.subject, body, confidence);

  return {
    messageId: envelope.messageId,
    threadId: envelope.threadId,
    subject: envelope.subject,
    from: envelope.from,
    date: envelope.date,
    confidence,
    eventType: eventType.type,
    eventConfidence: eventType.confidence,
    rulesFired,
    matchedKeywords,
    attachmentSignals,
    passed: confidence >= config.confidenceThreshold
  };
};

/**
 * Categorize email into event type
 */
const categorizeEvent = (subject, body, baseConfidence) => {
  const rules = SUBSCRIPTION_EMAIL_CONFIG.categorization.rules;
  const fullText = `${subject} ${body}`.toLowerCase();

  // Sort by priority (lower number = higher priority)
  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  for (const rule of sortedRules) {
    const subjectMatches = rule.subjectKeywords.filter(kw =>
      subject.toLowerCase().includes(kw.toLowerCase())
    ).length;

    const bodyMatches = rule.bodyKeywords.filter(kw =>
      fullText.includes(kw.toLowerCase())
    ).length;

    const totalMatches = subjectMatches + bodyMatches;
    const totalKeywords = rule.subjectKeywords.length + rule.bodyKeywords.length;

    // Calculate confidence as ratio of matches
    const matchRatio = totalMatches / totalKeywords;
    const eventConfidence = Math.min(1, matchRatio * 2) * baseConfidence;

    if (eventConfidence >= rule.minConfidence) {
      return {
        type: rule.type,
        confidence: eventConfidence,
        matches: { subject: subjectMatches, body: bodyMatches }
      };
    }
  }

  return {
    type: SUBSCRIPTION_EMAIL_CONFIG.categorization.fallbackType,
    confidence: baseConfidence,
    matches: { subject: 0, body: 0 }
  };
};

/**
 * Deduplicate messages
 */
export const deduplicateMessages = (scoredMessages) => {
  const config = SUBSCRIPTION_EMAIL_CONFIG.filtering.deduplication;
  const seen = new Set();
  const deduplicated = [];

  for (const msg of scoredMessages) {
    // Use message ID for exact deduplication
    if (config.useMessageId && msg.messageId) {
      if (seen.has(msg.messageId)) {
        continue;
      }
      seen.add(msg.messageId);
    }

    // Use thread ID for thread deduplication (optional)
    // Keep only highest confidence message per thread
    if (config.useThreadId && msg.threadId) {
      const threadKey = `thread:${msg.threadId}`;
      const existing = deduplicated.find(m => m.threadId === msg.threadId);

      if (existing) {
        if (msg.confidence > existing.confidence) {
          // Replace with higher confidence message
          const index = deduplicated.indexOf(existing);
          deduplicated[index] = msg;
        }
        continue;
      }
    }

    deduplicated.push(msg);
  }

  const removed = scoredMessages.length - deduplicated.length;
  console.log(`🗑️  Deduplicated: ${removed} duplicate messages removed`);

  return deduplicated;
};

/**
 * Main processing pipeline
 */
export const processGmailMessages = (messages) => {
  console.log(`\n🔬 Starting filtering pipeline for ${messages.length} messages...`);

  // Stage 1: Filter and score
  const scored = messages.map(filterAndScoreEmail);

  // Stage 2: Deduplicate
  const deduplicated = deduplicateMessages(scored);

  // Stage 3: Filter by confidence threshold
  const passed = deduplicated.filter(m => m.passed);
  const failed = deduplicated.filter(m => !m.passed);

  // Stage 4: Group by event type
  const byEventType = passed.reduce((acc, msg) => {
    acc[msg.eventType] = (acc[msg.eventType] || 0) + 1;
    return acc;
  }, {});

  console.log(`\n📊 Filtering Pipeline Results:`);
  console.log(`   Total processed: ${messages.length}`);
  console.log(`   Passed threshold: ${passed.length}`);
  console.log(`   Failed threshold: ${failed.length}`);
  console.log(`   Duplicates removed: ${messages.length - deduplicated.length}`);
  console.log(`\n   By Event Type:`);
  Object.entries(byEventType).forEach(([type, count]) => {
    console.log(`      ${type}: ${count}`);
  });

  return {
    passed,
    failed,
    stats: {
      total: messages.length,
      passed: passed.length,
      failed: failed.length,
      byEventType
    }
  };
};

export default {
  buildGmailSearchQueries,
  filterAndScoreEmail,
  deduplicateMessages,
  processGmailMessages,
  extractHtmlBody
};
